import { eTlfItemType } from "functions/api/enums";
import {
  isArsMasterReadDto,
  isTlfItemReadDto,
  TlfArsListingReadDto,
  TlfArsMasterReadDto,
  TlfArsTableReadDto,
} from "functions/api/tlfItem/TlfItemReadDto";
import { cloneDeep, omit } from "lodash";
import { checkChanged } from "./checkChanged";
import { ITlfActionSetVariantCreationView, TlfContextProps } from "./interface";
import { toArsTlfSnapshot } from "./toArsTlfSnapshot";
import { updateVariantPatchSelection } from "./updateVariantPatchSelection";

/**
 * Replaces the active TLF variant-creation mode. Entering promotes an ARS item to a master so
 * it can hold patches, and leaving demotes it when nothing
 * was designed. Neither counts as an unsaved change, as the view toggle only offers the mode
 * when the TLF matches what was saved.
 *
 * @param state - Current TLF context state.
 * @param action - Set-view-mode action containing the next mode.
 * @returns Updated state with the active view mode changed.
 */
export const tlf_setVariantCreationMode = (
  state: TlfContextProps,
  action: ITlfActionSetVariantCreationView,
): TlfContextProps => {
  const newState = { ...state };

  newState.variantCreationView = action.variantCreationView;
  newState.activeVariantPatchName =
    state.appliedVariantPatches.find((name) => name === state.activeVariantPatchName) ??
    state.appliedVariantPatches[0] ??
    null;

  const arsItem =
    isTlfItemReadDto(state.tlfData) &&
    (state.tlfData.type === eTlfItemType.arsTable || state.tlfData.type === eTlfItemType.arsListing)
      ? state.tlfData
      : null;

  if (action.variantCreationView && arsItem) {
    const promoted: TlfArsMasterReadDto = isArsMasterReadDto(arsItem)
      ? arsItem
      : { ...arsItem, allVariantPatches: {}, variantTemplates: [] };
    const master =
      state.appliedVariantPatches.length > 0
        ? (state.masterTlfData ?? toArsTlfSnapshot(promoted))
        : toArsTlfSnapshot(promoted);
    const current: TlfArsMasterReadDto = {
      ...(state.appliedVariantPatches.length > 0 ? promoted : master),
      allVariantPatches: promoted.allVariantPatches,
      variantTemplates: promoted.variantTemplates,
    };
    newState.tlfData = cloneDeep(current);
    newState.masterTlfData = cloneDeep(master);
  }
  // Clear the master TLF data when leaving variant creation mode and the master has no patches or variants
  else if (
    state.variantCreationView === true &&
    isArsMasterReadDto(state.tlfData) &&
    Object.keys(state.tlfData.allVariantPatches).length === 0 &&
    state.tlfData.variantTemplates.length === 0
  ) {
    newState.tlfData = omit(state.tlfData, ["allVariantPatches", "variantTemplates"]) as
      TlfArsTableReadDto | TlfArsListingReadDto;
    newState.masterTlfData = null;
  } else if (isArsMasterReadDto(state.tlfData)) {
    newState.masterTlfData = cloneDeep(
      state.appliedVariantPatches.length > 0
        ? (state.masterTlfData ?? toArsTlfSnapshot(state.tlfData))
        : toArsTlfSnapshot(state.tlfData),
    );
  }

  // Overwrite the current version so promoting or demoting is not an unsaved change
  if (newState.tlfData !== state.tlfData) {
    newState.tlfDataVersions = state.tlfDataVersions.map((version, ix) =>
      ix === state.ixTlfDataVersion ? cloneDeep(newState.tlfData) : version,
    );
    if (!action.variantCreationView && newState.masterTlfData === null) checkChanged(newState);
  }

  return updateVariantPatchSelection(
    newState,
    state.appliedVariantPatches,
    newState.activeVariantPatchName,
  );
};
